import PropTypes from 'prop-types'
import clsx from 'clsx'
import React, { forwardRef } from 'react'
import BaseButton from './BaseButton'
import { ButtonType, ButtonVariant, ButtonSize } from './Button.type'

const buttonProperties = {
  [ButtonType.SOLID]: {
    [ButtonVariant.NEUTRAL]: {
      className:
        'rounded-full no-underline text-grey-50 bg-transparent hover:text-grey-40 hover:bg-grey-90 focus:text-grey-40 focus:bg-grey-90 active:text-grey-10 active:bg-grey-70',
      disabledClassName:
        'text-grey-80! cursor-not-allowed! bg-transparent! hover:text-grey-80 hover:bg-transparent focus:text-grey-80 focus:bg-transparent active:text-grey-80 active:bg-transparent',
    },
    [ButtonVariant.DESTRUCTIVE]: {
      className:
        'rounded-full no-underline text-white bg-red-50 [&>button]:h-9 hover:bg-red-40 focus:bg-red-40 active:bg-red-20',
      disabledClassName:
        'text-grey-80! cursor-not-allowed! bg-grey-90! hover:bg-grey-90 focus:bg-grey-90 active:bg-grey-90',
    },
    [ButtonVariant.STANDARD]: {
      className:
        'rounded-full no-underline text-white bg-theme-50 [&>button]:h-9 hover:bg-theme-40 focus:bg-theme-40 active:bg-theme-20',
      disabledClassName:
        'text-grey-80! cursor-not-allowed! bg-grey-90! hover:bg-grey-90 focus:bg-grey-90 active:bg-grey-90',
    },
  },
  [ButtonType.GHOST]: {
    [ButtonVariant.NEUTRAL]: {
      className: 'rounded-full box-border no-underline [&>button]:h-9',
      disabledClassName: 'text-grey-80! cursor-not-allowed! bg-transparent!',
    },
    [ButtonVariant.DESTRUCTIVE]: {
      className:
        'rounded-full box-border no-underline text-red-50 [&>button]:h-9 hover:text-red-40 hover:bg-red-95 focus:text-red-40 focus:bg-red-95 active:text-red-20 active:bg-red-80',
      disabledClassName:
        'text-grey-80! cursor-not-allowed! bg-transparent! hover:text-grey-80 hover:bg-transparent focus:text-grey-80 focus:bg-transparent active:text-grey-80 active:bg-transparent',
    },
    [ButtonVariant.STANDARD]: {
      className:
        'rounded-full box-border no-underline text-theme-50 [&>button]:h-9 hover:text-theme-40 hover:bg-theme-95 focus:text-theme-40 focus:bg-theme-95 active:text-theme-20',
      disabledClassName:
        'text-grey-80! cursor-not-allowed! bg-transparent! hover:text-grey-80 hover:bg-transparent focus:text-grey-80 focus:bg-transparent active:text-grey-80 active:bg-transparent',
    },
  },
  [ButtonType.UTILITY]: {
    [ButtonVariant.UTILITY]: {
      className: 'DS__button-utility',
      disabledClassName: '',
    },
  },
  [ButtonType.CHIP]: {
    [ButtonVariant.CHIP]: {
      className: 'DS__button-chip',
      disabledClassName: '',
    },
  },
  [ButtonType.LINK]: {
    [ButtonVariant.STANDARD]: {
      className:
        'underline text-theme-50 p-0! [&_button]:p-0! font-medium hover:text-theme-40 focus:text-theme-40 active:text-theme-20',
      disabledClassName:
        'text-grey-80! cursor-not-allowed! hover:text-grey-80 focus:text-grey-80 active:text-grey-80',
    },
  },
}

const Button = forwardRef(
  (
    {
      dataTestId = 'button',
      type,
      variant,
      text,
      prefixIcon,
      suffixIcon,
      onClick,
      disabled,
      tooltipText,
      size,
      fullWidth = false,
      id,
      width,
      ...buttonProps
    },
    ref
  ) => {
    const buttonConfig = buttonProperties[type]?.[variant]

    if (!buttonConfig) {
      console.error(`Variant ${variant} is not supported for button type ${type}.`)
      return `Variant ${variant} is not supported for type ${type}`
    }

    const { className, disabledClassName } = buttonConfig

    const buttonClass = clsx(className, disabled && disabledClassName, {
      'type-meta-heavy px-2': size === ButtonSize.SMALL && type !== ButtonType.LINK,
      'type-body-1': size === ButtonSize.DEFAULT && type !== ButtonType.LINK,
    })

    const handleButtonClick = () => {
      if (onClick) {
        const event = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
        })
        onClick(event)
      }
    }

    return (
      <BaseButton
        ref={ref}
        dataTestId={dataTestId}
        text={text}
        prefixIcon={prefixIcon}
        suffixIcon={suffixIcon}
        onClick={handleButtonClick}
        btnClassName={buttonClass}
        disabled={disabled}
        tooltipText={tooltipText}
        btnType={type}
        fullWidth={fullWidth}
        id={id}
        width={width}
        {...buttonProps}
      />
    )
  }
)

Button.displayName = 'Button'

Button.propTypes = {
  dataTestId: PropTypes.string,
  type: PropTypes.oneOf(Object.values(ButtonType)).isRequired,
  variant: PropTypes.oneOf(Object.values(ButtonVariant)).isRequired,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  prefixIcon: PropTypes.node,
  suffixIcon: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  tooltipText: PropTypes.string,
  size: PropTypes.oneOf(Object.values(ButtonSize)),
  fullWidth: PropTypes.bool,
  id: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default Button
